import React, {
  useCallback,
  useContext,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'

import BaseBookmarkButton from '../Common/BaseBookmarkButton'
import SaveModal from '../Common/SaveModal'
import {
  getOfferImageId,
  getLabelText,
  getPropertyName,
  getSavedDetailsForOffer,
} from './utils'

import { accountAccessShowLogin } from 'actions/UiActions'
import GeoContext from 'contexts/geoContext'
import { RecommendationSaveClickTrackerContext } from 'contexts/RecommendationSaveClickTrackerContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import useReduxStateListener from 'hooks/useReduxStateListener'
import useToggle from 'hooks/useToggle'
import { getBedbankPriceAndValue } from 'lib/bedbank/bedbankUtils'
import { buildSuggestedDatesParamsKey, parseOccupancy } from 'lib/searchUtils'
import { parseSearchString } from 'lib/urlUtils'
import { isLoggedIn } from 'selectors/accountSelectors'
import type { AppDispatch } from 'src/client/store'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import {
  useCreateTripItemBatch,
  useDeleteTripItem,
  useEditableTrips,
  useTrip,
} from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import {
  setCurrentSectionId,
  setTripItemHasJustBeenAdded,
} from 'tripPlanner/reducers/actions'
import {
  getImmersiveTripId,
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import { BookmarkDatesAndOccupancy } from 'tripPlanner/types/tripItem'
import { createBedbankItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/bedbank'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  datesAndGuests: BookmarkDatesAndOccupancy
  roomTypeId?: string
  testId?: string
}

interface MappedStateProps {
  suggestedDatesByOfferId: { [offerId: string]: App.OfferSuggestedDates }
}

const INITIAL_CLICK_SCHEDULE = 0

function BedbankBaseBookmarkButton({
  offer,
  datesAndGuests,
  roomTypeId,
  testId,
  suggestedDatesByOfferId,
}: Props & MappedStateProps) {
  const suggestedDates = suggestedDatesByOfferId?.[offer.id]

  const checkIn = datesAndGuests?.checkInDate ?? suggestedDates?.checkIn
  const checkOut = datesAndGuests?.checkOutDate ?? suggestedDates?.checkOut
  const inTripId = useTripId()
  const currentTripId = useSelector(getImmersiveTripId) ?? inTripId
  const { data, isFetching: tripsFetching } = useEditableTrips()
  const trips = data as BasicTrip[]

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { currentCurrency, currentRegionCode, showTaxExclusivePrices } =
    useContext(GeoContext)
  const dispatch = useDispatch<AppDispatch>()

  const details = getSavedDetailsForOffer(trips, offer.id, roomTypeId)
  const isSaved = details.length > 0

  const saveClickTracker = useContext(RecommendationSaveClickTrackerContext)

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res, vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveSuccessSnackbar(trip.id, trip.name)
    },
    onError: (_res, _vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveErrorSnackbar(trip.name)
    },
  })

  const {
    value: isModalOpen,
    toggle: toggleModal,
    off: closeModal,
  } = useToggle()
  const newTripName = useNewTripNameFromOffer(offer)

  const [hotelRates] = useBedbankRates(
    offer.id,
    datesAndGuests.occupancies,
    checkIn,
    checkOut,
  )

  const { price, value, isExclusivePrice } = useMemo(
    () =>
      getBedbankPriceAndValue(hotelRates, showTaxExclusivePrices, roomTypeId),
    [hotelRates, showTaxExclusivePrices, roomTypeId],
  )

  const propertyName = getPropertyName(offer)

  const { mutate: createItem, isLoading: isCreating } = useCreateTripItemBatch({
    onSuccess: (response) => {
      const itemId = response.updatedItemIds[0]
      showSaveSuccessSnackbar(response.id, response.name, itemId)
      dispatch(setCurrentSectionId(itemSectionId(itemId)))
      dispatch(setTripItemHasJustBeenAdded())
      closeModal()
    },
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(propertyName)
    },
  })

  const createTripItems = useCallback(
    (trip: BasicTrip | FullTrip) => {
      const payload = createBedbankItemPayload({
        offer,
        datesAndGuests,
        currentRegionCode,
        currencyCode: currentCurrency,
        price,
        value,
        roomTypeId,
        taxExclusivePricing: isExclusivePrice || false,
        templateId,
        templateItemId,
      })
      createItem({ tripId: trip.id, tripItems: [payload] })
    },
    [
      offer,
      datesAndGuests,
      currentRegionCode,
      currentCurrency,
      price,
      value,
      createItem,
      roomTypeId,
      isExclusivePrice,
      templateId,
      templateItemId,
    ],
  )
  // #region These states are used to repeat the button click after logging in

  const loggedIn = useSelector(isLoggedIn)
  const loginSelector = (state: App.State) => {
    if (isLoggedIn(state)) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  }

  const loginListener = useReduxStateListener<{ loggedIn: boolean }>(
    loginSelector,
  )

  const prevForcedButtonClickScheduleHandled = useRef(INITIAL_CLICK_SCHEDULE)
  const [forcedButtonClickSchedule, setForcedButtonClick] = useState(
    INITIAL_CLICK_SCHEDULE,
  )

  // #endregion

  const onButtonClick = useCallback(() => {
    // Trips api fails with 401 if you are not logged in, so we will force
    // a login and repeat the button click after
    if (!loggedIn) {
      dispatch(accountAccessShowLogin())
      loginListener.awaitResult().then(({ loggedIn }) => {
        if (loggedIn) {
          setForcedButtonClick((prevValue) => prevValue + 1)
        }
      })
    } else if (isSaved) {
      Promise.all(
        details.map((detail) =>
          deleteBookmark({
            tripId: detail.trip.id,
            tripItemId: detail.itemId,
          }),
        ),
      )
    } else if (currentTrip) {
      createTripItems(currentTrip)
      saveClickTracker()
    } else {
      toggleModal()
    }
  }, [
    createTripItems,
    currentTrip,
    deleteBookmark,
    details,
    dispatch,
    isSaved,
    loggedIn,
    loginListener,
    toggleModal,
    saveClickTracker,
  ])

  // #endregion This is used to handle the scheduled repeat button click after logging in

  useEffect(() => {
    if (tripsFetching) {
      return
    }
    if (
      forcedButtonClickSchedule > prevForcedButtonClickScheduleHandled.current
    ) {
      prevForcedButtonClickScheduleHandled.current = forcedButtonClickSchedule
      if (!isSaved) {
        onButtonClick()
      }
    }
  }, [tripsFetching, isSaved, forcedButtonClickSchedule, onButtonClick])

  // Disable button while we trying to find out if the button should create a trip or delete a trip
  const disableWhileForcedClickScheduled =
    forcedButtonClickSchedule > prevForcedButtonClickScheduleHandled.current &&
    tripsFetching

  // #endregion

  return (
    <>
      <BaseBookmarkButton
        label={getLabelText(isSaved, !!roomTypeId)}
        isProcessing={
          isDeleting || isCreating || disableWhileForcedClickScheduled
        }
        isSaved={isSaved}
        onClick={onButtonClick}
        testId={testId}
      />
      <SaveModal
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
        createTripItems={createTripItems}
        offerType="Hotel"
        defaultTripName={newTripName}
        offerImageId={getOfferImageId(offer)}
        isCreatingItem={isCreating}
      />
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState) => {
    const searchString = appState.router.location.search
    const occupancies = parseOccupancy(searchString)
    const { flexibleMonths, flexibleNights } = parseSearchString(searchString)
    const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(
      flexibleMonths,
      flexibleNights,
      occupancies,
    )

    return {
      suggestedDatesByOfferId:
        appState.offer.searchResultMetadata.offerSuggestedDates?.[
          flexibleSearchFilterKey
        ],
    }
  },
)(BedbankBaseBookmarkButton)
